import { useCallback } from "react";
import * as THREE from "three";
import { ZoomIn, ZoomOut, RotateCcw, RotateCw, ArrowLeft, ArrowRight, ArrowUp, ArrowDown, Home, Eye } from "lucide-react";

interface CameraControlsButtonsProps {
  controlsRef: React.RefObject<any>;
}

export function CameraControlsButtons({ controlsRef }: CameraControlsButtonsProps) {
  const nudge = useCallback((action: string) => {
    const controls = controlsRef.current;
    if (!controls) return;

    const camera = controls.object;
    const target = controls.target;

    switch (action) {
      case "zoom-in":
        camera.position.lerp(target, 0.15);
        controls.update();
        break;
      case "zoom-out": {
        const dir = camera.position.clone().sub(target).normalize();
        camera.position.add(dir.multiplyScalar(0.4));
        controls.update();
        break;
      }
      case "rotate-left": {
        controls.autoRotate = false;
        const axis = new THREE.Vector3(0, 1, 0);
        const offset = camera.position.clone().sub(target);
        offset.applyAxisAngle(axis, 0.15);
        camera.position.copy(target).add(offset);
        controls.update();
        break;
      }
      case "rotate-right": {
        controls.autoRotate = false;
        const axis2 = new THREE.Vector3(0, 1, 0);
        const offset2 = camera.position.clone().sub(target);
        offset2.applyAxisAngle(axis2, -0.15);
        camera.position.copy(target).add(offset2);
        controls.update();
        break;
      }
      case "pan-left":
        target.x -= 0.15;
        camera.position.x -= 0.15;
        controls.update();
        break;
      case "pan-right":
        target.x += 0.15;
        camera.position.x += 0.15;
        controls.update();
        break;
      case "pan-up":
        target.y += 0.15;
        camera.position.y += 0.15;
        controls.update();
        break;
      case "pan-down":
        target.y -= 0.15;
        camera.position.y -= 0.15;
        controls.update();
        break;
      case "reset":
        controls.reset();
        break;
      case "top-view": {
        const dist = camera.position.clone().sub(target).length();
        camera.position.set(target.x, target.y + dist, target.z + 0.01);
        camera.lookAt(target);
        controls.update();
        break;
      }
    }
  }, [controlsRef]);

  const btnClass =
    "w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all duration-150 active:scale-90";

  return (
    <div className="flex flex-col gap-1 p-2 rounded-xl bg-black/60 backdrop-blur-xl border border-white/10">
      <span className="text-[9px] tracking-[0.2em] uppercase text-white/40 text-center mb-1">Camera</span>

      {/* Zoom */}
      <div className="flex items-center justify-center gap-0.5">
        <button onClick={() => nudge("zoom-in")} title="Zoom in" className={btnClass}>
          <ZoomIn size={14} />
        </button>
        <button onClick={() => nudge("zoom-out")} title="Zoom out" className={btnClass}>
          <ZoomOut size={14} />
        </button>
      </div>

      {/* Top view */}
      <div className="flex items-center justify-center">
        <button onClick={() => nudge("top-view")} title="Top view" className={btnClass}>
          <Eye size={14} />
        </button>
      </div>

      <div className="h-px bg-white/5" />

      {/* Rotate */}
      <div className="flex items-center justify-center gap-0.5">
        <button onClick={() => nudge("rotate-left")} title="Rotate left" className={btnClass}>
          <RotateCcw size={14} />
        </button>
        <button onClick={() => nudge("rotate-right")} title="Rotate right" className={btnClass}>
          <RotateCw size={14} />
        </button>
      </div>

      <div className="h-px bg-white/5" />

      {/* Pan */}
      <div className="flex flex-col items-center gap-0.5">
        <button onClick={() => nudge("pan-up")} title="Pan up" className={btnClass}>
          <ArrowUp size={14} />
        </button>
        <div className="flex items-center gap-0.5">
          <button onClick={() => nudge("pan-left")} title="Pan left" className={btnClass}>
            <ArrowLeft size={14} />
          </button>
          <button onClick={() => nudge("reset")} title="Reset view" className={btnClass}>
            <Home size={14} />
          </button>
          <button onClick={() => nudge("pan-right")} title="Pan right" className={btnClass}>
            <ArrowRight size={14} />
          </button>
        </div>
        <button onClick={() => nudge("pan-down")} title="Pan down" className={btnClass}>
          <ArrowDown size={14} />
        </button>
      </div>
    </div>
  );
}
